import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { AiSupportInstructionProperties } from '@server/ai';
import orderBy from 'lodash/orderBy';
import {
  AiAssistantConversation,
  ConversationStatus,
} from '../components/pages/ai-assistant/types';
import { SfAppElement } from '../components/pages/ai-assistant/apps/AppsConnectionDialog';

export interface AiSupportState {
  [crmOrgId: string]: {
    instructions?: AiSupportInstructionProperties[];
    aiAssistantConversations?: AiAssistantConversation[];
    apps?: SfAppElement[];
  };
}

const initialState: AiSupportState = {};

export const aiSupportSlice = createSlice({
  name: 'aiSupport',
  initialState,
  reducers: {
    setApps: (state, action: PayloadAction<{ crmOrgId: string; apps: SfAppElement[] }>) => {
      if (!state[action.payload.crmOrgId]) {
        state[action.payload.crmOrgId] = {};
      }
      state[action.payload.crmOrgId].apps = action.payload.apps;
    },
    setInstructions: (
      state,
      action: PayloadAction<{ crmOrgId: string; instructions: AiSupportInstructionProperties[] }>,
    ) => {
      const oldState = state[action.payload.crmOrgId];
      state[action.payload.crmOrgId] = {
        ...(oldState ?? {}),
        instructions: orderBy(action.payload.instructions, 'createdAt', 'desc'),
      };
    },
    setAiAssistantConversations: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        aiAssistantConversations: AiAssistantConversation[];
      }>,
    ) => {
      if (!state[action.payload.crmOrgId]) {
        state[action.payload.crmOrgId] = {
          aiAssistantConversations: action.payload.aiAssistantConversations,
        };
      } else {
        state[action.payload.crmOrgId].aiAssistantConversations =
          action.payload.aiAssistantConversations;
      }
    },
    markConversationAsResolved: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        chatId: string;
        status: ConversationStatus;
      }>,
    ) => {
      const conversations = state[action.payload.crmOrgId]?.aiAssistantConversations;
      if (!conversations) return;

      const index = conversations.findIndex(
        (conversation) => conversation.id === action.payload.chatId,
      );

      if (index !== -1) {
        conversations[index].status = action.payload.status;
      }
    },

    addInstruction: (
      state,
      action: PayloadAction<{ crmOrgId: string; instruction: AiSupportInstructionProperties }>,
    ) => {
      state[action.payload.crmOrgId].instructions = [
        action.payload.instruction,
        ...(state[action.payload.crmOrgId].instructions ?? []),
      ];
    },
    updateInstruction: (
      state,
      action: PayloadAction<{ crmOrgId: string; instruction: AiSupportInstructionProperties }>,
    ) => {
      const instructions = state[action.payload.crmOrgId].instructions;
      if (!instructions) return;

      const index = instructions.findIndex(
        (instruction) => instruction.id === action.payload.instruction.id,
      );
      if (index !== -1) {
        instructions[index] = action.payload.instruction;
      }
    },
    removeInstruction: (
      state,
      action: PayloadAction<{ crmOrgId: string; instructionId: string }>,
    ) => {
      const instructions = state[action.payload.crmOrgId].instructions;
      if (!instructions) return;

      state[action.payload.crmOrgId].instructions = instructions.filter(
        (instruction) => instruction.id !== action.payload.instructionId,
      );
    },
  },
});

export const {
  setInstructions,
  addInstruction,
  updateInstruction,
  removeInstruction,
  setAiAssistantConversations,
  markConversationAsResolved,
  setApps,
} = aiSupportSlice.actions;

export const selectAiSupportInstructions = (crmOrgId: string) => (state: RootState) =>
  state.aiSupport[crmOrgId]?.instructions;

export const selectAiAssistantConversations = (crmOrgId: string) => (state: RootState) =>
  state.aiSupport[crmOrgId]?.aiAssistantConversations;

export const selectApps = (crmOrgId: string) => (state: RootState) =>
  state.aiSupport[crmOrgId]?.apps;

export default aiSupportSlice.reducer;
